/*
 * This module contains functions to access the timeline endpoints.
 * It exposes common behaviour that is shared betweed 2D and 3D timeline tools.
 * Client code should probably use either timeline-api.js oar timeline-3d-api.js
 * and not directly this.
 */
import type { Color } from '@disy/lib/colors';
import { getColorWithOpacity } from '@disy/lib/colors';

import type { TimelineDto, TimelineStyleDto } from 'cadenza/api-client/timeline-dto';
import { resolveReference } from 'cadenza/map/style/resolve-style-reference';

export function uniqueTimelineId(timeline: TimelineDto) {
  return `${timeline.layerId}:timeline:${timeline.timelineId}`;
}

/*
 * The color of the timeline is dependent on the styling of all the points in that track. (this
 * timeline style is for example used in the activity line in the player and in the 3D representation)
 * If the points all have the same foregroundColor then this color will be used for the track. In
 * addition, if all the foregroundOpacities are the same, then we also use this opacity for the track.
 */
export function determineTimelineColor(timeline: TimelineDto, originalStyles: TimelineStyleDto[]) {
  const styleReferences = [...new Set(timeline.points.map(([, , style]) => style.$ref))];
  const pointsStyles = styleReferences.map((styleRef) => resolveReference(originalStyles, styleRef));

  const timelineColor = getPropertyValueIfSameInAllStyles(pointsStyles, 'foregroundColor') as Color | false;
  const timelineColorOpacity = getPropertyValueIfSameInAllStyles(pointsStyles, 'foregroundColorOpacity');
  if (timelineColor && timelineColorOpacity) {
    return getColorWithOpacity(timelineColor, timelineColorOpacity as number);
  }
  return timelineColor;
}

function getPropertyValueIfSameInAllStyles<P extends keyof TimelineStyleDto>(
  styles: TimelineStyleDto[],
  propertyName: P,
) {
  let propertyValue: TimelineStyleDto[P] | false = false;
  for (let i = 0; i < styles.length; i++) {
    if (styles[i][propertyName]) {
      if (!propertyValue) {
        propertyValue = styles[i][propertyName];
      } else if (propertyValue !== styles[i][propertyName]) {
        return false;
      }
    }
  }
  return propertyValue;
}
